/**
 * /pipeline-facility/sites — the customer's pipelines and facilities
 * (GET /pipeline-facility/sites, fieldiq-api#1196).
 *
 * Full-width register; a row opens the site on its own page. Filters live in
 * the URL (kind, customer, status, search) so the back arrow returns to the
 * list as it was left. Kind, customer and status filter server-side.
 *
 * OD and MOP arrive in the tenant's units and are labelled from `meta.units`;
 * nothing is converted here. The counts are a tally of the unfiltered,
 * unpaginated list (no endpoint returns one). The customer filter's options
 * come from that same list, so a field hand (who cannot read /customers)
 * still gets them.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AlertTriangle, Factory, GitCommitHorizontal, MapPin, Plus, Search } from 'lucide-react';
import { PageHeaderBand } from '../../components/layout/PageHeaderBand';
import { useAuth } from '../../contexts/AuthContext';
import { displayValue } from '../../features/fieldiq-handoff/contracts';
import { usePfMeta, usePfSites } from '../../hooks/usePipelineFacility';
import { queryLoadFailure } from '../../lib/load-failure';
import { canAdminPf, fmtNumber, pfStatusSkin, withUnit } from '../../lib/pipeline-facility';
import type { PfSite } from '../../types/pipeline-facility';
import { useOpenRecord } from '../downhole/recordPage';
import { PF_EYEBROW, PF_INPUT, PfBadge, PfChip, PfChipRow, PfEmpty, PfPanel, PfRegister, type PfColumn } from './PfBits';

export default function PfSitesScreen() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const openRecord = useOpenRecord();
  const [searchParams, setSearchParams] = useSearchParams();
  const kind = searchParams.get('kind') ?? '';
  const customerId = searchParams.get('customerId') ?? '';
  const status = searchParams.get('status') ?? '';
  const qParam = searchParams.get('q') ?? '';
  const [search, setSearch] = useState(qParam);

  const setParam = (key: string, next: string) => {
    const p = new URLSearchParams(searchParams);
    if (next) p.set(key, next);
    else p.delete(key);
    setSearchParams(p, { replace: true });
  };

  useEffect(() => {
    const t = setTimeout(() => {
      if (search.trim() !== qParam) setParam('q', search.trim());
    }, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const meta = usePfMeta();
  const catalog = meta.data?.data ?? null;
  const filters = useMemo(() => ({ kind, customerId, status, q: qParam }), [kind, customerId, status, qParam]);
  const filtered = !!(kind || customerId || status || qParam);
  const listQuery = usePfSites(filters);
  const allQuery = usePfSites();
  const sites = listQuery.data?.data ?? [];
  const units = listQuery.data?.meta.units ?? allQuery.data?.meta.units ?? null;
  const all = allQuery.isLoading || queryLoadFailure(allQuery) ? undefined : allQuery.data?.data;
  const countOf = (pick: (s: PfSite) => boolean) => displayValue(all ? all.filter(pick).length : undefined);
  const countsLoading = allQuery.isLoading;
  const canAdd = canAdminPf(user?.role);

  const customers = useMemo(() => {
    const seen = new Map<string, string>();
    (all ?? []).forEach((s) => seen.set(s.customerId, s.customerName ?? '—'));
    return Array.from(seen, ([id, name]) => ({ id, name })).sort((a, b) => a.name.localeCompare(b.name));
  }, [all]);

  const kindLabel = (s: PfSite) => s.kindLabel ?? catalog?.siteKinds.find((k) => k.value === s.kind)?.label ?? s.kind ?? '—';

  const columns: PfColumn<PfSite>[] = [
    {
      key: 'name',
      header: 'Site',
      render: (s) => (
        <>
          <p className="font-extrabold text-white">{s.name || '—'}</p>
          <p className="mt-0.5 text-xs text-fieldiq-muted">{s.lsd || '—'}</p>
        </>
      ),
    },
    { key: 'kind', header: 'Kind', render: kindLabel },
    { key: 'customer', header: 'Customer', render: (s) => s.customerName || '—' },
    {
      key: 'licence',
      header: 'Licence / line',
      render: (s) => (
        <>
          <p className="font-mono text-sm">{s.licenceNumber || '—'}</p>
          <p className="mt-0.5 font-mono text-xs text-fieldiq-muted">{s.lineNumber || '—'}</p>
        </>
      ),
    },
    { key: 'province', header: 'Prov / state', render: (s) => s.provinceState || '—' },
    { key: 'product', header: 'Product', render: (s) => s.productLabel || '—' },
    { key: 'sour', header: 'Sour', render: (s) => (s.sour ? <span className="font-bold text-fieldiq-text">Sour</span> : 'Sweet') },
    { key: 'od', header: `OD${units?.diameter ? ` (${units.diameter})` : ''}`, align: 'right', render: (s) => fmtNumber(s.od) },
    { key: 'mop', header: `MOP${units?.pressure ? ` (${units.pressure})` : ''}`, align: 'right', render: (s) => fmtNumber(s.mop) },
    { key: 'segments', header: 'Segments', align: 'right', render: (s) => (s.kind === 'pipeline' ? displayValue(s.segmentCount ?? undefined) : '—') },
    { key: 'status', header: 'Status', render: (s) => <PfBadge skin={pfStatusSkin(s.status)} value={s.status} /> },
  ];

  return (
    <div data-testid="pf-sites-page" className="animate-fade-in min-h-full bg-fieldiq-dark">
      <PageHeaderBand
        eyebrow={PF_EYEBROW}
        title="Sites"
        onBack={() => navigate(-1)}
        backLabel="Back"
        actions={
          canAdd ? (
            <button type="button" onClick={() => openRecord('/pipeline-facility/sites/new')} className="fi-action fi-action--standard fi-action--primary">
              <Plus className="size-4" aria-hidden="true" />
              New site
            </button>
          ) : undefined
        }
        stats={[
          { icon: <MapPin className="size-4" aria-hidden="true" />, label: 'Sites', sub: 'Registered', tone: 'neutral', loading: countsLoading, value: countOf(() => true) },
          { icon: <GitCommitHorizontal className="size-4" aria-hidden="true" />, label: 'Pipelines', tone: 'neutral', loading: countsLoading, value: countOf((s) => s.kind === 'pipeline') },
          { icon: <Factory className="size-4" aria-hidden="true" />, label: 'Facilities', tone: 'neutral', loading: countsLoading, value: countOf((s) => s.kind === 'facility') },
          { icon: <AlertTriangle className="size-4" aria-hidden="true" />, label: 'Sour', sub: 'H2S service', tone: 'accent', loading: countsLoading, value: countOf((s) => s.sour) },
        ]}
      />

      <section className="px-[var(--fi-page-inline)] py-[var(--fi-page-block)] pb-[var(--bottom-chrome)]">
        <div className="mx-auto max-w-[1920px] space-y-4">
          <PfChipRow label="Filter by kind">
            <PfChip label="All" active={!kind} onClick={() => setParam('kind', '')} count={countsLoading ? undefined : countOf(() => true)} />
            {(catalog?.siteKinds ?? []).map((k) => (
              <PfChip
                key={k.value}
                label={k.label}
                active={kind === k.value}
                onClick={() => setParam('kind', k.value)}
                count={countsLoading ? undefined : countOf((s) => s.kind === k.value)}
              />
            ))}
          </PfChipRow>
          <div className="grid gap-3 sm:grid-cols-[minmax(0,1fr)_14rem_12rem]">
            <label className="relative block">
              <span className="sr-only">Search name, licence, line, LSD or customer</span>
              <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-fieldiq-muted" aria-hidden="true" />
              <input
                type="search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search name, licence, line, LSD or customer"
                className="h-10 w-full rounded-fi-control border border-fieldiq-border bg-fieldiq-card pl-9 pr-3 text-sm text-white placeholder:text-fieldiq-muted focus:border-fi-amber focus:outline-none"
              />
            </label>
            <select className={PF_INPUT} aria-label="Filter by customer" value={customerId} onChange={(e) => setParam('customerId', e.target.value)}>
              <option value="">All customers</option>
              {customers.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
            <select className={PF_INPUT} aria-label="Filter by status" value={status} onChange={(e) => setParam('status', e.target.value)}>
              <option value="">Any status</option>
              {(catalog?.siteStatuses ?? []).map((s) => (
                <option key={s.value} value={s.value}>
                  {s.label}
                </option>
              ))}
            </select>
          </div>

          <PfPanel
            eyebrow="Site register"
            title="Pipelines & facilities"
            testId="pf-site-register"
            footer={
              !listQuery.isLoading && !queryLoadFailure(listQuery) && sites.length > 0
                ? filtered
                  ? `Showing ${sites.length} of ${countOf(() => true)} sites`
                  : `${sites.length} site${sites.length === 1 ? '' : 's'}`
                : undefined
            }
          >
            <PfRegister
              query={listQuery}
              rows={sites}
              columns={columns}
              rail={(s) => pfStatusSkin(s.status).rail}
              onOpen={(s) => openRecord(`/pipeline-facility/sites/${s.id}`)}
              subject="sites"
              testId="pf-site"
              minWidth="min-w-[1100px]"
              empty={filtered ? <PfEmpty>No site matches these filters.</PfEmpty> : <PfEmpty>There are no pipeline or facility sites yet.</PfEmpty>}
              card={(s) => (
                <>
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="truncate text-sm font-extrabold text-white">{s.name || '—'}</p>
                      <p className="mt-0.5 text-xs text-fieldiq-muted">
                        {kindLabel(s)} · {s.customerName || '—'}
                      </p>
                    </div>
                    <PfBadge skin={pfStatusSkin(s.status)} value={s.status} />
                  </div>
                  <p className="mt-2 text-xs text-fieldiq-text">
                    {s.licenceNumber || '—'} · OD {withUnit(s.od, units?.diameter)} · MOP {withUnit(s.mop, units?.pressure)}
                    {s.sour ? ' · Sour' : ''}
                  </p>
                </>
              )}
            />
          </PfPanel>
        </div>
      </section>
    </div>
  );
}
